import React, { useEffect, useState } from 'react';
import {
  LOG_PAGE_SIZE,
  kindLabel,
  reminderError,
  useReminderAssignments,
  useReminderLog,
} from '../../hooks/useCohortReminders';
import {
  CARD,
  CHANNELS,
  INPUT,
  STATUSES,
  formatDateTime,
} from './reminderText';

const STATUS_STYLES = {
  queued: 'bg-green-50 text-green-700',
  skipped: 'bg-gray-100 text-gray-600',
  failed: 'bg-red-50 text-red-700',
};

const label = (list, key) =>
  list.find((item) => item.key === key)?.label || key;

// Every reminder handed to a channel, skipped (with why) or failed.
export default function ReminderLog({ cohortId, hours = [] }) {
  const [filters, setFilters] = useState({});
  const [studentInput, setStudentInput] = useState('');
  const [offset, setOffset] = useState(0);
  const { data: assignments = [] } = useReminderAssignments(cohortId);
  const { data, isLoading, error } = useReminderLog(cohortId, filters, offset);
  const entries = data?.entries ?? [];
  const total = data?.total ?? 0;

  const set = (changes) => {
    setFilters((f) => ({ ...f, ...changes }));
    setOffset(0);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if ((filters.studentId || '') !== studentInput.trim())
        set({ studentId: studentInput.trim() });
    }, 400);
    return () => clearTimeout(timer);
    // set and filters are read fresh when the timer fires.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentInput]);

  // The cohort's reminders, plus any older kinds the log shows (hours change).
  const kinds = [
    ...new Set([
      ...hours.map((h) => `due_${h}h`),
      'missed',
      ...entries.map((e) => e.kind).filter(Boolean),
    ]),
  ];

  return (
    <section className={`${CARD} space-y-4 p-5`}>
      <div>
        <p className="text-sm font-semibold text-gray-900">Delivery log</p>
        <p className="text-xs text-gray-500">
          Each learner gets each reminder once per channel. Skipped ones say
          why.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <select
          aria-label="Assignment"
          value={filters.assignmentId || ''}
          onChange={(e) => set({ assignmentId: e.target.value })}
          className={`${INPUT} max-w-xs`}
        >
          <option value="">All assignments</option>
          {assignments.map((a) => (
            <option key={a.assignment_id} value={a.assignment_id}>
              {a.title}
              {a.module_name ? ` (${a.module_name})` : ''}
            </option>
          ))}
        </select>
        <select
          aria-label="Reminder"
          value={filters.kind || ''}
          onChange={(e) => set({ kind: e.target.value })}
          className={INPUT}
        >
          <option value="">All reminders</option>
          {kinds.map((k) => (
            <option key={k} value={k}>
              {kindLabel(k)}
            </option>
          ))}
        </select>
        <select
          aria-label="Channel"
          value={filters.channel || ''}
          onChange={(e) => set({ channel: e.target.value })}
          className={INPUT}
        >
          <option value="">All channels</option>
          {CHANNELS.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Status"
          value={filters.status || ''}
          onChange={(e) => set({ status: e.target.value })}
          className={INPUT}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
        <input
          type="search"
          aria-label="Student ID"
          placeholder="Student ID"
          value={studentInput}
          onChange={(e) => setStudentInput(e.target.value)}
          className={`${INPUT} w-56 font-mono`}
        />
      </div>

      {isLoading && (
        <div
          className="h-24 rounded-lg bg-gray-100 animate-pulse"
          aria-label="Loading"
        />
      )}
      {error && !data && (
        <p className="text-sm text-gray-600">
          {reminderError(error, "We couldn't load the delivery log.")}
        </p>
      )}
      {data && total === 0 && (
        <p className="text-sm text-gray-600">Nothing has been sent yet.</p>
      )}
      {data && total > 0 && (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                  <th className="py-2 pl-4 pr-2 font-medium">When</th>
                  <th className="px-2 py-2 font-medium">Learner</th>
                  <th className="px-2 py-2 font-medium">Assignment</th>
                  <th className="px-2 py-2 font-medium">Reminder</th>
                  <th className="px-2 py-2 font-medium">Channel</th>
                  <th className="py-2 pl-2 pr-4 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr
                    key={`${e.student_id}-${e.assignment_id}-${e.kind}-${e.channel}`}
                    className="border-b border-gray-100 last:border-0"
                  >
                    <td className="whitespace-nowrap py-2 pl-4 pr-2 text-gray-700">
                      {formatDateTime(e.at)}
                    </td>
                    <td className="px-2 py-2">
                      <span className="text-gray-900">
                        {e.student_name || e.student_id}
                      </span>
                      {e.student_email && (
                        <span className="block text-xs text-gray-500">
                          {e.student_email}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <span className="text-gray-900">
                        {e.assignment_title || e.assignment_id}
                      </span>
                      {e.module_name && (
                        <span className="block text-xs text-gray-500">
                          {e.module_name}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-gray-700">
                      {kindLabel(e.kind)}
                    </td>
                    <td className="px-2 py-2 text-gray-700">
                      {label(CHANNELS, e.channel)}
                    </td>
                    <td className="py-2 pl-2 pr-4">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          STATUS_STYLES[e.status] || 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {label(STATUSES, e.status)}
                      </span>
                      {e.detail && (
                        <span className="block text-xs text-gray-500">
                          {e.detail}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>
              {(offset + 1).toLocaleString()}–
              {Math.min(offset + entries.length, total).toLocaleString()} of{' '}
              {total.toLocaleString()}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - LOG_PAGE_SIZE))}
                className="rounded-md border border-gray-300 px-3 py-1 hover:bg-gray-50 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={offset + LOG_PAGE_SIZE >= total}
                onClick={() => setOffset(offset + LOG_PAGE_SIZE)}
                className="rounded-md border border-gray-300 px-3 py-1 hover:bg-gray-50 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
